import { FlexContainer } from '@/ui/styled-ui';
import {
  colorBlackMain,
  colorPurpleText,
  colorRedLight,
  colorRedMain,
  platformFontFamily,
} from '@/utils/helpers/css-variables.helper';
import { Chart } from 'primereact/chart';
import React, { useEffect, useState } from 'react';
import { tooltipDefaultSettings } from '../charts.helper';

export type LineChartProps = {
  id: string;
  values: (number | undefined)[];
  labels: string[];
  isPurple?: boolean;
};

export const LineChart: React.FC<LineChartProps> = ({
  id,
  values,
  labels,
  isPurple = false,
}) => {
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});
  const docStyle = getComputedStyle(document.documentElement);

  useEffect(() => {
    const data = {
      labels,
      datasets: [
        {
          data: values,
          fill: false,
          tension: 0.1,
          borderWidth: 1,
          borderColor: isPurple
            ? `${colorPurpleText(docStyle)}66`
            : colorRedLight(docStyle),
          pointBackgroundColor: isPurple
            ? colorPurpleText(docStyle)
            : colorRedMain(docStyle),
          pointStyle: 'rectRounded',
          pointRadius: 8,
          pointHoverRadius: 8,
          pointBorderWidth: 0,
          clip: false,
        },
      ],
    };

    const validValues = values.filter(
      (v): v is number => typeof v === 'number' && Number.isFinite(v),
    );
    const yMin =
      validValues.length > 0 ? Math.max(0, Math.min(...validValues) - 5) : 0;
    const yMax =
      validValues.length > 0
        ? Math.min(100, Math.max(...validValues) + 5)
        : 100;

    const options = {
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
          position: 'nearest',
          yAlign: 'bottom',
          ...tooltipDefaultSettings(),
          callbacks: {
            title: () => [],
            label: (tooltipItem: any) => {
              const score = data.datasets[0].data[tooltipItem.dataIndex];
              if (score == null) return '';
              return `${score}/100`;
            },
          },
        },
        datalabels: {
          display: false,
        },
      },
      scales: {
        x: {
          ticks: {
            display: true,
            color: colorBlackMain(docStyle),
            font: { size: 14, family: platformFontFamily(docStyle) },
          },
          grid: {
            display: true,
            drawBorder: false,
            borderDash: [5, 5],
          },
          border: {
            display: false,
            dash: [5, 5],
          },
          offset: true,
        },
        y: {
          ticks: {
            maxTicksLimit: 6,
            callback: (value: number) => Math.round(value),
            color: colorBlackMain(docStyle),
            font: { size: 14, family: platformFontFamily(docStyle) },
            padding: 12,
          },
          grid: {
            display: true,
            drawBorder: false,
            borderDash: [5, 5],
          },
          border: {
            display: false,
            dash: [5, 5],
          },
          min: yMin,
          max: yMax,
        },
      },
      responsive: true,
      maintainAspectRatio: false,
    };

    setChartData(data);
    setChartOptions(options);
  }, [values, isPurple]);

  return (
    <FlexContainer direction="column" width="100%">
      <div className="demo-overlay w-full">
        <Chart
          id={id}
          type="line"
          data={chartData}
          options={chartOptions}
          className="chart"
          width="100%"
          height="180px"
        />
      </div>
    </FlexContainer>
  );
};
